import type { ProgrammaticLandingPageConfig } from '../../types'
import {
  STORAGE_REFERENCE_KWP,
  SELF_CONSUMPTION_WITHOUT_BATTERY,
  SELF_CONSUMPTION_BATTERY_BONUS,
  SELF_CONSUMPTION_WITH_BATTERY,
  BATTERY_COST_PER_KWH_RANGE_EUR,
} from '../storageSizeFacts'

export const KWH_10_MAGAZYN_ENERGII: ProgrammaticLandingPageConfig = {
  slug: 'magazyn-energii-10-kwh-cena',
  lang: 'pl',
  topicId: 'kwh-speicher-10',
  title: 'Magazyn energii 10 kWh: cena i wartości orientacyjne 2026',
  metaDescription:
    'Ile kosztuje magazyn energii 10 kWh? Dla kogo się nadaje, wartości orientacyjne ' +
    'autokonsumpcji i bezpłatne porównanie ofert na instalację + magazyn.',
  keywords: [
    'magazyn energii 10 kWh cena',
    'magazyn energii 10 kWh koszt',
    'bateria fotowoltaiczna 10 kWh',
    'ile kosztuje magazyn energii 10 kWh',
  ],
  eyebrow: 'Wielkość magazynu: 10 kWh',
  h1: 'Magazyn energii 10 kWh: cena i wartości orientacyjne',
  intro:
    `Magazyn energii 10 kWh to najczęstsze połączenie z instalacją ${STORAGE_REFERENCE_KWP} kWp - ` +
    'obie wielkości wynikają z powszechnej w branży praktycznej reguły proporcji 1:1 kWp:kWh. ' +
    'Znajdziesz tu wartości orientacyjne dotyczące odpowiedniej wielkości gospodarstwa, ' +
    'autokonsumpcji z magazynem energii, a tuż poniżej kalkulator z już wypełnioną średnią ceną ' +
    'dla tego połączenia, obliczoną na podstawie prawdziwych ofert firm rzemieślniczych.',
  calculatorPrefill: { kind: 'kwp+storage', kwp: STORAGE_REFERENCE_KWP, kwh: 10 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: 'Dla kogo odpowiedni jest magazyn energii 10 kWh?' },
    {
      type: 'paragraph',
      text:
        `Wartość orientacyjna: 10 kWh uznaje się za standardowe połączenie z instalacją ` +
        `${STORAGE_REFERENCE_KWP} kWp. Przy pompie ciepła lub regularnym ładowaniu samochodu ` +
        'elektrycznego warto rozważyć również 15 lub 20 kWh.',
    },
    { type: 'heading', text: 'Autokonsumpcja z magazynem energii' },
    {
      type: 'paragraph',
      text:
        `Bez magazynu udział autokonsumpcji w instalacji ${STORAGE_REFERENCE_KWP} kWp wynosi ` +
        `zwykle ok. ${SELF_CONSUMPTION_WITHOUT_BATTERY * 100} %. Z odpowiednio dobranym magazynem ` +
        `10 kWh udział ten rośnie w modelu obliczeniowym o ${SELF_CONSUMPTION_BATTERY_BONUS * 100} ` +
        `punktów procentowych, do ok. ${SELF_CONSUMPTION_WITH_BATTERY * 100} % - przy dodatkowej ` +
        'pompie ciepła lub samochodzie elektrycznym, aż do granicy 85 %. Dokładne obliczenie dla ' +
        'Twojego zużycia daje kalkulator doboru instalacji.',
    },
    { type: 'heading', text: 'Zasilanie awaryjne' },
    {
      type: 'paragraph',
      text:
        'Magazyn energii 10 kWh ze skrzynką backup zwykle zapewnia wystarczającą pojemność, by ' +
        'zasilić centralne odbiorniki przez kilka godzin, a nawet cały dzień - dokładny czas ' +
        'zależy od rzeczywistego zużycia podłączonych urządzeń. Czy i w jakim zakresie dana oferta ' +
        'obejmuje zasilanie awaryjne, zależy od konkretnej firmy - warto o to zapytać bezpośrednio.',
    },
    { type: 'heading', text: 'Koszty magazynu i dofinansowanie' },
    {
      type: 'paragraph',
      text:
        'Koszty magazynów energii mocno się różnią w zależności od producenta i złożoności ' +
        'montażu - badanie rynku z 2026 roku pokazuje przedział od ok. ' +
        `${BATTERY_COST_PER_KWH_RANGE_EUR.min} do ${BATTERY_COST_PER_KWH_RANGE_EUR.max.toLocaleString('pl-PL')} ` +
        '€ za kWh. W Niemczech istnieją regionalne dofinansowania specjalnie na magazyny energii ' +
        '(np. program kraju związkowego Berlin z dopłatą do magazynów energii); w Austrii system ' +
        'EAG dolicza 150 €/kWh za magazyn - dokładne obliczenie dla Twojego połączenia daje ' +
        'kalkulator poniżej.',
    },
  ],
  faq: [
    {
      q: 'Dlaczego 10 kWh to najczęstsze połączenie z instalacją 10 kWp?',
      a: 'Ponieważ obie wielkości wynikają z powszechnej w branży fotowoltaicznej praktycznej reguły proporcji 1:1 między kWp a kWh - dobry punkt wyjścia, choć nie sztywna zasada. Dokładną rekomendację dla Twojego zużycia daje kalkulator doboru instalacji.',
    },
    {
      q: 'Czy 10 kWh wystarczy dla domu jednorodzinnego bez pompy ciepła i samochodu elektrycznego?',
      a: 'W większości przypadków tak - 10 kWh to standardowa wielkość dla domów jednorodzinnych z przeciętnym zużyciem. Przy pompie ciepła lub samochodzie elektrycznym warto rozważyć 15 lub 20 kWh.',
    },
    {
      q: 'Czy magazyn 10 kWh musi być zawsze łączony z instalacją 10 kWp?',
      a: `Nie, ${STORAGE_REFERENCE_KWP} kWp jest tu jedynie wielkością referencyjną do przedstawienia ceny. Przy innej wielkości instalacji kalkulator doboru instalacji daje odpowiednią rekomendację dla Twojego dachu.`,
    },
    {
      q: 'Czy 10 kWh wystarczy na pełną samowystarczalność w nocy?',
      a: 'W wielu przypadkach tak, nawet przy nieco wyższym zużyciu niż w przeciętnym gospodarstwie domowym. Dokładną ocenę dla Twojego rzeczywistego zużycia daje kalkulator doboru instalacji.',
    },
  ],
  primaryCta: { label: 'Poproś teraz o bezpłatną ofertę', action: 'customer' },
  secondaryCta: {
    label: 'Koszt instalacji fotowoltaicznej: pełny przewodnik',
    action: 'link',
    href: '/koszt-instalacji-fotowoltaicznej',
  },
  relatedLinks: [
    { label: 'Magazyn energii 7 kWh: cena i wartości orientacyjne', href: '/magazyn-energii-7-kwh-cena' },
    { label: 'Magazyn energii 15 kWh: cena i wartości orientacyjne', href: '/magazyn-energii-15-kwh-cena' },
    { label: 'Instalacja fotowoltaiczna 10 kWp: cena i wartości orientacyjne', href: '/instalacja-fotowoltaiczna-10-kwp-cena' },
    { label: 'Jak działa porównanie ofert', href: '/porownaj-oferty-fotowoltaiczne' },
  ],
}
